import { DateInput } from '../../../components/DateInput/DateInput';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import introducedInvestorService, { Step1RequestDto } from '../../../services/introducedInvestorService';
import './IntroducedInvestorRegistration.scss';
import { PremiumSelect } from '../../../components/PremiumSelect/PremiumSelect';
import { contentService } from '../../../services/content.service';
import { CompactHeader } from '../../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../../components/CompactFooter/CompactFooter';
import { RegistrationStepper } from '../../../components/RegistrationStepper/RegistrationStepper';
import { RegistrationVisualCard } from '../../../components/RegistrationVisualCard/RegistrationVisualCard';

// Dropdown values are UPPERCASE by convention (see V12__uppercase_dropdown_values.sql),
// so the option value and the stored DB value match and the select re-renders on reload.
const GENDER_OPTIONS = [
  { value: 'MALE', label: 'MALE' },
  { value: 'FEMALE', label: 'FEMALE' },
  { value: 'TRANSGENDER', label: 'TRANSGENDER' },
];

// Fallback used only until the ISD list loads from the backend (/master/isd-codes).
const COUNTRY_CODE_OPTIONS = [
  { value: '+91', label: '+91 (India)', selectedLabel: '+91' },
  { value: '+1', label: '+1 (USA)', selectedLabel: '+1' },
  { value: '+44', label: '+44 (UK)', selectedLabel: '+44' },
  { value: '+971', label: '+971 (UAE)', selectedLabel: '+971' },
];

/**
 * Step 2: Personal Details Form + Send OTP
 * Matches Laravel: introduce-register-step1.blade.php
 */
const IntroducedInvestorStep1: React.FC = () => {
  const { uniqueCode } = useParams<{ uniqueCode: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(true);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [countryCodeOptions, setCountryCodeOptions] = useState(COUNTRY_CODE_OPTIONS);
  const [formData, setFormData] = useState<Step1RequestDto>({
    uniqueCode: uniqueCode!,
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    mobileNumber: '',
    countryCode: '+91',
    countryOfResidence: '',
    ociCardStatus: 'Yes',
    panCardStatus: 'Yes',
    indianOrigin: 'Yes',
    agreeForOtp: false
  });
  // Country of Residency comes from the introduction and is not the investor's to choose here,
  // so it is shown read-only. The OCI question follows the same India rule as Step 4 did.
  const [residenceLabel, setResidenceLabel] = useState('');
  const [nationalityIsIndia, setNationalityIsIndia] = useState<boolean | null>(null);
  // Fields the introduction prefilled (non-empty) are locked like Country of Residency:
  // they are the SP's introduction data, not the investor's to change here. Fields the
  // introduction left blank (e.g. middle name) stay editable.
  const [prefilledFields, setPrefilledFields] = useState<Set<string>>(new Set());
  const isPrefilled = (field: string) => prefilledFields.has(field);
  const lockStyle = { backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' } as const;
  const lockTitle = 'Taken from your introduction and cannot be changed here';

  // Fetch session prefill data (email, name) on mount
  useEffect(() => {
    const fetchPrefill = async () => {
      try {
        setPrefillLoading(true);
        const response = await introducedInvestorService.getSessionPrefill(uniqueCode!);
        const data = response.data;
        setFormData(prev => ({
          ...prev,
          email: data.email || '',
          firstName: data.firstName || '',
          middleName: data.middleName || '',
          lastName: data.lastName || '',
          // Digits only, as typing would give: the field is locked, so a stored "58 123..."
          // could never be corrected here and the server accepts digits only.
          mobileNumber: (data.mobile || '').replace(/[^0-9]/g, ''),
          // Preselect the investor's country dialing code from Dataverse (e.g. "+65").
          countryCode: data.countryCode || prev.countryCode,
          countryOfResidence: data.countryOfResidenceGuid || prev.countryOfResidence,
          ociCardStatus: data.ociCardStatus || prev.ociCardStatus,
          panCardStatus: data.panCardStatus || prev.panCardStatus,
          indianOrigin: data.indianOrigin || prev.indianOrigin
        }));
        setNationalityIsIndia(data.nationalityIsIndia === true);

        // Lock exactly the fields the introduction supplied (mirrors Country of Residency).
        const locked = new Set<string>();
        if (data.firstName) locked.add('firstName');
        if (data.middleName) locked.add('middleName');
        if (data.lastName) locked.add('lastName');
        if (data.email) locked.add('email');
        if (data.mobile) locked.add('mobileNumber');
        if (data.countryCode) locked.add('countryCode');
        setPrefilledFields(locked);

        // Display the server-resolved name, NOT a lookup of countryOfResidenceGuid.
        // That GUID is the ISD/dial-code reference, which can name a different country than
        // the investor actually resides in — a Singapore resident whose ISD reference points
        // at India would have been shown "India". The posted value stays the ISD reference so
        // the backend resolves it exactly as before; only what is displayed changes.
        setResidenceLabel(data.countryOfResidenceName ?? '');
      } catch (err: any) {
        console.error('Error fetching session prefill:', err);
      } finally {
        setPrefillLoading(false);
      }
    };
    if (uniqueCode) {
      fetchPrefill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueCode]);

  // Load the full country dialing-code list (matches Laravel's master_country_of_residence dropdown).
  useEffect(() => {
    contentService.getIsdCodes()
      .then(list => {
        const opts = (list || [])
          .filter(c => c.codeValue != null)
          // Country name in the list (it is what makes 200+ codes findable), code alone in the
          // closed field — "+44 (United Kingdom)" does not fit the ISD box (user 2026-09-11).
          .map(c => ({
            value: `+${c.codeValue}`,
            label: `+${c.codeValue} (${c.countryName ?? ''})`.trim(),
            selectedLabel: `+${c.codeValue}`,
          }));
        if (opts.length) {
          setCountryCodeOptions(opts);
        }
      })
      .catch(err => console.error('Error fetching ISD codes:', err));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validationErrors: Record<string, string> = {};

    if (!formData.firstName?.trim()) {
      validationErrors.firstName = 'First name is required';
    }
    if (!formData.lastName?.trim()) {
      validationErrors.lastName = 'Last name is required';
    }
    if (!formData.dateOfBirth) {
      validationErrors.dateOfBirth = 'Date of birth is required';
    }
    if (!formData.gender) {
      validationErrors.gender = 'Gender is required';
    }
    if (!formData.email?.trim()) {
      validationErrors.email = 'Email is required';
    }
    // No minimum length: the number comes locked from the introduction, so a length rule
    // here could only strand the investor on a field they cannot change.
    if (!formData.mobileNumber?.trim()) {
      validationErrors.mobileNumber = 'Mobile number is required';
    }
    if (!formData.agreeForOtp) {
      validationErrors.agreeForOtp = 'Please agree to receive OTP on your email';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await introducedInvestorService.submitStep1(formData);

      if (response.data.success) {
        navigate(`/investor/introduced/step2/${uniqueCode}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit details');
      console.error('Error submitting Step1:', err);
    } finally {
      setLoading(false);
    }
  };

  if (prefillLoading) {
    return <div className="loading-container">Loading registration form...</div>;
  }

  return (
    <>
      <CompactHeader />
      <section className="login-form-style4 steps4-sec section-padding align-items-center">
        <div className="container">
          <div className="registration-split-layout">
            {/* Left Panel: Form & Stepper */}
            <div className="left-panel">
              {/* Stepper Progress */}
              <RegistrationStepper currentStep={3} title="Registration Details" maxWidth="850px" />

              {/* Investor Registration Card */}
              <div className="login-form-style3-main" style={{ width: '100%', maxWidth: '850px', margin: '0 auto', textAlign: 'left' }}>
                <div className="login-form-style3-main_full">
                  {error && <div className="alert alert-danger">{error}</div>}

                  <div className="login-register3-form-middle">
                    <form onSubmit={handleSubmit} noValidate>
                      <input type="hidden" name="unique_code" value={uniqueCode} />
                      <input type="hidden" name="version" value="1.0" />

                      <div className="single-field">
                        <label htmlFor="firstName">
                          First Name: <span className="star-color">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          id="firstName"
                          className="form-control"
                          value={formData.firstName}
                          onChange={handleChange}
                          readOnly={isPrefilled('firstName')}
                          disabled={isPrefilled('firstName')}
                          title={isPrefilled('firstName') ? lockTitle : undefined}
                          style={{ textTransform: 'uppercase', ...(isPrefilled('firstName') ? lockStyle : {}) }}
                        />
                        {errors.firstName && <span role="alert">{errors.firstName}</span>}
                      </div>

                      <div className="single-field">
                        <label htmlFor="middleName">Middle Name:</label>
                        <input
                          type="text"
                          name="middleName"
                          id="middleName"
                          className="form-control"
                          value={formData.middleName}
                          onChange={handleChange}
                          readOnly={isPrefilled('middleName')}
                          disabled={isPrefilled('middleName')}
                          title={isPrefilled('middleName') ? lockTitle : undefined}
                          style={{ textTransform: 'uppercase', ...(isPrefilled('middleName') ? lockStyle : {}) }}
                        />
                      </div>

                      <div className="single-field">
                        <label htmlFor="lastName">
                          Last Name: <span className="star-color">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          id="lastName"
                          className="form-control"
                          value={formData.lastName}
                          onChange={handleChange}
                          readOnly={isPrefilled('lastName')}
                          disabled={isPrefilled('lastName')}
                          title={isPrefilled('lastName') ? lockTitle : undefined}
                          style={{ textTransform: 'uppercase', ...(isPrefilled('lastName') ? lockStyle : {}) }}
                        />
                        {errors.lastName && <span role="alert">{errors.lastName}</span>}
                      </div>

                      <div className="single-field">
                        <label htmlFor="dateOfBirth">
                          DOB: <span className="star-color">*</span>
                        </label>
                        <DateInput
                          name="dateOfBirth"
                          id="dateOfBirth"
                          className="form-control"
                          value={formData.dateOfBirth}
                          onChange={(value) => {
                            setFormData(prev => ({ ...prev, dateOfBirth: value }));
                            if (errors.dateOfBirth) {
                              setErrors(prev => {
                                const next = { ...prev };
                                delete next.dateOfBirth;
                                return next;
                              });
                            }
                          }}
                          max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                        />
                        {errors.dateOfBirth && <span role="alert">{errors.dateOfBirth}</span>}
                      </div>

                      <div className="single-field">
                        <label htmlFor="gender">
                          Gender: <span className="star-color">*</span>
                        </label>
                        <PremiumSelect
                          value={formData.gender}
                          onChange={(val) => {
                            setFormData(prev => ({ ...prev, gender: val }));
                            if (errors.gender) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.gender;
                                return newErrors;
                              });
                            }
                          }}
                          options={GENDER_OPTIONS}
                          placeholder="Select Gender"
                        />
                        {errors.gender && <span role="alert">{errors.gender}</span>}
                      </div>

                      <div className="single-field">
                        <label htmlFor="email">
                          Please enter your Email: <span className="star-color">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          className="form-control"
                          value={formData.email}
                          readOnly
                          disabled={isPrefilled('email')}
                          title={isPrefilled('email') ? lockTitle : undefined}
                          style={isPrefilled('email') ? lockStyle : undefined}
                        />
                        {errors.email && <span role="alert">{errors.email}</span>}
                      </div>

                      <div className="single-field">
                        <label htmlFor="mobileNumber">
                          Please enter your Mobile No: <span className="star-color">*</span>
                        </label>
                        <div className="mobile-input-row">
                          <PremiumSelect
                            value={formData.countryCode}
                            onChange={(val) => setFormData(prev => ({ ...prev, countryCode: val }))}
                            options={countryCodeOptions}
                            className="country-code-select"
                            disabled={isPrefilled('countryCode')}
                            searchable
                          />
                          <input
                            type="text"
                            name="mobileNumber"
                            id="mobileNumber"
                            className="form-control"
                            value={formData.mobileNumber}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              setFormData(prev => ({ ...prev, mobileNumber: value }));
                              if (errors.mobileNumber) {
                                setErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.mobileNumber;
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="Enter mobile number"
                            maxLength={16}
                            readOnly={isPrefilled('mobileNumber')}
                            disabled={isPrefilled('mobileNumber')}
                            title={isPrefilled('mobileNumber') ? lockTitle : undefined}
                            style={isPrefilled('mobileNumber') ? lockStyle : undefined}
                          />
                        </div>
                        {errors.mobileNumber && <span role="alert">{errors.mobileNumber}</span>}
                      </div>

                      {/* Country of Residency — set by the introduction, shown read-only.
                          The reference is still posted so the backend resolves it exactly as before. */}
                      <div className="single-field">
                        <label htmlFor="countryOfResidence">
                          <i className="fa-solid fa-user"></i> Country of Residency: <span className="star-color">*</span>
                          <i className="fa-solid fa-circle-info"
                             title="Taken from your introduction and cannot be changed here"
                             style={{ marginLeft: '4px', color: '#2c5966', cursor: 'help' }}></i>
                        </label>
                        <input
                          type="text"
                          id="countryOfResidence"
                          value={residenceLabel || (prefillLoading ? 'Loading…' : '—')}
                          readOnly
                          disabled
                          aria-readonly="true"
                          style={{ backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' }}
                        />
                      </div>

                      {/* PAN applies only to an India nationality — the twin of the OCI question
                          below. Both are identity answers that feed investor-type derivation, so
                          they belong together on Step 1 rather than split across two steps. */}
                      {nationalityIsIndia === true && (
                        <div className="single-field self-sec1">
                          <label htmlFor="panCard">
                            <i className="fa-solid fa-address-card"></i> Do you have PAN card? <span className="star-color">*</span>
                            <i className="fa-solid fa-circle-info"
                               title="Permanent Account Number (PAN) is a ten-digit tax identification alphanumeric number, issued by Income-tax Department, Government of India"
                               style={{ marginLeft: '4px', color: '#2c5966', cursor: 'help' }}></i>
                          </label>
                          <div className="radio-box">
                            <label className="radio">
                              <input name="panCardStatus" type="radio" value="Yes"
                                checked={formData.panCardStatus === 'Yes'}
                                onChange={() => setFormData(prev => ({ ...prev, panCardStatus: 'Yes' }))} />
                              <span>Yes</span>
                            </label>
                            <label className="radio">
                              <input name="panCardStatus" type="radio" value="No"
                                checked={formData.panCardStatus === 'No'}
                                onChange={() => setFormData(prev => ({ ...prev, panCardStatus: 'No' }))} />
                              <span>No</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Person of Indian Origin — asked, not assumed. Step 4 used to hardcode
                          this to "Yes", which classified every non-Indian investor as OCI.
                          Laravel's nextholder and self-register flows ask it; only the introduce
                          flow lost the input, leaving a dead change1() script behind. */}
                      {nationalityIsIndia === false && (
                        <div className="single-field self-sec1">
                          <label htmlFor="indianOrigin">
                            <i className="fa-solid fa-user"></i> Are you a person of Indian Origin? <span className="star-color">*</span>
                            <i className="fa-solid fa-circle-info"
                               title="A person of Indian origin holds a foreign passport but has Indian ancestry"
                               style={{ marginLeft: '4px', color: '#2c5966', cursor: 'help' }}></i>
                          </label>
                          <div className="radio-box">
                            <label className="radio">
                              <input name="indianOrigin" type="radio" value="Yes"
                                checked={formData.indianOrigin === 'Yes'}
                                onChange={() => setFormData(prev => ({ ...prev, indianOrigin: 'Yes' }))} />
                              <span>Yes</span>
                            </label>
                            <label className="radio">
                              <input name="indianOrigin" type="radio" value="No"
                                checked={formData.indianOrigin === 'No'}
                                onChange={() => setFormData(prev => ({ ...prev, indianOrigin: 'No' }))} />
                              <span>No</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* OCI applies only to a non-India nationality — the same rule Step 4 used.
                          Rendered only once the flag is known, so it never flickers in and out. */}
                      {nationalityIsIndia === false && (
                        <div className="single-field self-sec1">
                          <label htmlFor="ociCard">
                            <i className="fa-solid fa-address-card"></i> Do you have an OCI Card: <span className="star-color">*</span>
                            <i className="fa-solid fa-circle-info"
                               title="OCI card is issued by High Commission/Consulate of India, where you reside through an application process"
                               style={{ marginLeft: '4px', color: '#2c5966', cursor: 'help' }}></i>
                          </label>
                          <div className="radio-box">
                            <label className="radio">
                              <input
                                name="ociCardStatus"
                                type="radio"
                                value="Yes"
                                checked={formData.ociCardStatus === 'Yes'}
                                onChange={() => setFormData(prev => ({ ...prev, ociCardStatus: 'Yes' }))}
                              />
                              <span>Yes</span>
                            </label>
                            <label className="radio">
                              <input
                                name="ociCardStatus"
                                type="radio"
                                value="No"
                                checked={formData.ociCardStatus === 'No'}
                                onChange={() => setFormData(prev => ({ ...prev, ociCardStatus: 'No' }))}
                              />
                              <span>No</span>
                            </label>
                          </div>
                        </div>
                      )}

                      <div className="single-field">
                        <div className="checkbox-wrapper-33">
                          <label className="checkbox">
                            <input
                              className="checkbox__trigger visuallyhidden"
                              type="checkbox"
                              name="agreeForOtp"
                              checked={formData.agreeForOtp}
                              onChange={handleChange}
                            />
                            <span className="checkbox__symbol">
                              <svg aria-hidden="true" className="icon-checkbox" width="28px" height="28px" viewBox="0 0 28 28" version="1" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 14l8 7L24 7"></path>
                              </svg>
                            </span>
                            <p className="checkbox__textwrapper">
                              I agree to receive the OTP on my email <span className="star-color">*</span>
                            </p>
                          </label>
                        </div>
                        {errors.agreeForOtp && <span role="alert">{errors.agreeForOtp}</span>}
                      </div>

                      <div className="single-field mb-0 text-center border-t pt-3 mt-3">
                        <button
                          className="button-1"
                          id="submitBtn"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Creative Illustration Panel */}
            <div className="right-panel">
              <RegistrationVisualCard step="details" registerAs={1} />
            </div>
          </div>
        </div>
      </section>
      <CompactFooter />
    </>
  );
};

export default IntroducedInvestorStep1;
